import React, { useMemo, useState } from 'react';
import { Box, Button, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { getAttendanceData } from '../api/employeeApi';
import { resolveAttendanceGeo } from '../api/geofenceApi';
import { AttendanceValidation } from '../types/geofenceTypes';
import { useSnackbar } from '../contexts/SnackbarContext';

interface AttendanceRecord {
    rut: string;
    fullName: string;
    entrada: string;
    salida: string;
    registeredHours: string;
}

const AttendanceManager: React.FC = () => {
  const { showMessage } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);

  // Registros geolocalizados resueltos por geocerca (asignación de CC).
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoRecords, setGeoRecords] = useState<AttendanceValidation[]>([]);

  // Filtro de texto (aplica a ambas tablas).
  const [search, setSearch] = useState<string>('');

  // Fechas por defecto (Ej: Hoy)
  const today = new Date().toISOString().split('T')[0];
  const [desde, setDesde] = useState<string>(today);
  const [hasta, setHasta] = useState<string>(today);

  const handleFetchAttendance = async () => {
    if (!desde || !hasta) return;
    setLoading(true);
    try {
      const data = await getAttendanceData(desde, hasta);
      setRecords(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveGeo = async () => {
    if (!desde || !hasta) return;
    setGeoLoading(true);
    try {
      const data = await resolveAttendanceGeo(desde, hasta);
      setGeoRecords(data);
      if (data.length === 0) {
        showMessage('No se encontraron registros geolocalizados en el rango.', 'info');
      }
    } catch (error: any) {
      showMessage(error?.response?.data?.mensaje ?? 'Error al resolver los marcajes por geocerca.', 'error');
    } finally {
      setGeoLoading(false);
    }
  };

  const estadoLabel = (resolution?: string) => {
    if (resolution === 'DENTRO_DE_OBRA') return 'Asignado';
    if (resolution === 'SIN_ASIGNAR') return 'Sin geocerca';
    return 'Sin coordenada';
  };

  const resolutionChip = (rec: AttendanceValidation) => {
    if (rec.resolution === 'DENTRO_DE_OBRA') {
      return <Chip label="Asignado" size="small" color="success" />;
    }
    if (rec.resolution === 'SIN_ASIGNAR') {
      return <Chip label="Sin geocerca" size="small" color="error" variant="outlined" />;
    }
    return <Chip label="Sin coordenada" size="small" color="warning" variant="outlined" />;
  };

  // --- Filtrado por texto ---
  const s = search.trim().toLowerCase();
  const filteredRecords = useMemo(
    () => (!s ? records : records.filter((r) => `${r.rut} ${r.fullName}`.toLowerCase().includes(s))),
    [records, s]
  );
  const filteredGeoRecords = useMemo(
    () =>
      !s
        ? geoRecords
        : geoRecords.filter((r) =>
            `${r.dni ?? ''} ${r.sentido ?? ''} ${r.projectName ?? ''} ${r.costCenter ?? ''} ${estadoLabel(r.resolution)}`
              .toLowerCase()
              .includes(s)
          ),
    [geoRecords, s]
  );

  // --- Exportar a Excel ---
  const exportAttendanceExcel = () => {
    if (filteredRecords.length === 0) {
      showMessage('No hay registros para exportar.', 'info');
      return;
    }
    const rows = filteredRecords.map((r) => ({
      RUT: r.rut,
      Nombre: r.fullName,
      Entrada: r.entrada,
      Salida: r.salida,
      'Horas Registradas': r.registeredHours,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asistencia');
    XLSX.writeFile(wb, `Asistencia_${desde}_a_${hasta}.xlsx`);
  };

  const exportGeoExcel = () => {
    if (filteredGeoRecords.length === 0) {
      showMessage('No hay registros para exportar.', 'info');
      return;
    }
    const rows = filteredGeoRecords.map((r) => ({
      DNI: r.dni,
      'Fecha/Hora': r.markedAt ? r.markedAt.replace('T', ' ') : '',
      Sentido: r.sentido,
      Latitud: r.latitud,
      Longitud: r.longitud,
      Estado: estadoLabel(r.resolution),
      Proyecto: r.projectName ?? '',
      'Centro de Coste': r.costCenter ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asignacion CC');
    XLSX.writeFile(wb, `CC_por_geocerca_${desde}_a_${hasta}.xlsx`);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1', mr: 1 }}>
          Control de Asistencia
        </Typography>

        <TextField
          label="Desde"
          type="date"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Hasta"
          type="date"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" onClick={handleFetchAttendance} disabled={loading || !desde || !hasta}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar Registros'}
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleResolveGeo}
          disabled={geoLoading || !desde || !hasta}
        >
          {geoLoading ? <CircularProgress size={24} color="inherit" /> : 'Asignar CC por geocerca'}
        </Button>

        {/* Filtro de texto para ambas tablas */}
        <TextField
          label="Filtrar (RUT, nombre, proyecto, CC…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260, ml: { md: 'auto' } }}
        />
      </Box>

      {/* Tabla de asistencia */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">
          Registros: {filteredRecords.length}
          {s && records.length !== filteredRecords.length ? ` de ${records.length}` : ''}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={exportAttendanceExcel}
          disabled={filteredRecords.length === 0}
          sx={{ ml: 'auto' }}
        >
          Exportar Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600, boxShadow: 3 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>RUT</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Nombre Empleado</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Entrada</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Salida</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Horas Registradas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((rec, index) => (
                <TableRow key={`${rec.rut}-${index}`} hover>
                  <TableCell>{rec.rut}</TableCell>
                  <TableCell>{rec.fullName}</TableCell>
                  <TableCell align="center">{rec.entrada}</TableCell>
                  <TableCell align="center">{rec.salida}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={rec.registeredHours}
                      size="small"
                      color={rec.registeredHours.includes('En turno') ? 'warning' : 'primary'}
                      variant={rec.registeredHours.includes('00:00') ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    {records.length === 0
                      ? 'Selecciona un rango de fechas y presiona "Buscar Registros".'
                      : 'Ningún registro coincide con el filtro.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Asignación de centro de coste por geolocalización (geocerca) */}
      {geoRecords.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
              Asignación de CC por geocerca ({filteredGeoRecords.length}
              {s && geoRecords.length !== filteredGeoRecords.length ? ` de ${geoRecords.length}` : ''})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={exportGeoExcel}
              disabled={filteredGeoRecords.length === 0}
              sx={{ ml: 'auto' }}
            >
              Exportar Excel
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 600, boxShadow: 3 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>DNI</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Fecha/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Sentido</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Lat</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Long</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Proyecto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Centro de Coste</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGeoRecords.map((rec, index) => (
                  <TableRow key={`${rec.dni}-${index}`} hover>
                    <TableCell>{rec.dni}</TableCell>
                    <TableCell>{rec.markedAt ? rec.markedAt.replace('T', ' ') : '—'}</TableCell>
                    <TableCell>{rec.sentido}</TableCell>
                    <TableCell align="center">{rec.latitud}</TableCell>
                    <TableCell align="center">{rec.longitud}</TableCell>
                    <TableCell align="center">{resolutionChip(rec)}</TableCell>
                    <TableCell>{rec.projectName ?? '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{rec.costCenter ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceManager;
